import ModalConfirm from 'components/Modal/ModalConfirm';
import { CALENDAR_TIME_SET } from 'features/appointment/services/constants';
import appointmentSelectors from 'features/appointment/services/selectors';
import moment from 'moment';
import React, { memo, useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { momentTimezone } from 'utils/time';
import CalendarViewBody from './Body';
import HeadContent from './HeadContent';
import CalendarViewHeader from './Header';
import LoadingCalendar from './Loading';
import useCalendarFilter, { modalConfirmRef } from './hooks/useCalendarFilter';
import CalendarStyled, { PIXEL_PER_MINUTE } from './styles';
import shopSelectors from 'services/shop/selectors';

const getTop = (dateStr?: string) => { //TODO
  const { close, open } = CALENDAR_TIME_SET;
  const now = dateStr ? momentTimezone(dateStr) : moment();
  const currentSpaceMinutes = now.diff(open, 'minutes');
  let space = 0;
  if (now.isBetween(open, close))
    space = PIXEL_PER_MINUTE * currentSpaceMinutes;
  return space;
};

type ICalendarTableViewProps = {
  modal?: boolean;
  v2?: boolean;
};
const CalendarTableView: React.FC<ICalendarTableViewProps> = ({ modal, v2 }) => {
  const calendarRef = useRef<HTMLTableElement>(null);
  const schema = useCalendarFilter({ modal });
  const startTime = appointmentSelectors.syncTimeItem();
  const enableDrag = appointmentSelectors.enableDrag();

  const isDragging = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const scrollLeft = useRef(0);
  const scrollTop = useRef(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (enableDrag) return;
    if (!calendarRef.current) return;
    isDragging.current = true;
    calendarRef.current.classList.add('grabbing');

    startX.current = e.pageX - calendarRef.current.offsetLeft;
    startY.current = e.pageY - calendarRef.current.offsetTop;
    scrollLeft.current = calendarRef.current.scrollLeft;
    scrollTop.current = calendarRef.current.scrollTop;
  };

  const handleMouseUpOrLeave = () => {
    if (enableDrag) return;
    isDragging.current = false;
    calendarRef.current?.classList.remove('grabbing');
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (enableDrag) return;
    if (!isDragging.current || !calendarRef.current) return;
    e.preventDefault();
    const x = e.pageX - calendarRef.current.offsetLeft;
    const y = e.pageY - calendarRef.current.offsetTop;

    const walkX = x - startX.current;
    const walkY = y - startY.current;

    calendarRef.current.scrollLeft = scrollLeft.current - walkX;
    calendarRef.current.scrollTop = scrollTop.current - walkY;
  };


  const scrollTo = (top: number) => {
    setTimeout(() => calendarRef.current?.scrollTo({ top: top, behavior: 'smooth' }), 0);
  };

  const onDragStart = () => {
    isDragging.current = true;
  };

  const onDragEnd = () => {
    isDragging.current = false;
  };

  useEffect(() => {
    scrollTo(getTop(startTime));
  }, []);

  return (
    <>
      {!modal && (!v2 && <HeadContent />)}
      <CalendarTableViewStyled id='calendar' className={v2 ? 'v2' : ''}>
        <CalendarTableOverflowStyled ref={calendarRef}
          className='drag-scroll-container'
          id='drag-scroll-container'
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseUpOrLeave}
          onMouseUp={handleMouseUpOrLeave}
          onMouseMove={handleMouseMove}
        >
          <div className="calendar">
            <CalendarStyled.Table typeView={schema.typeView} >
              <CalendarViewHeader schema={schema} />
              <CalendarViewBody modal={modal} schema={schema} onDragStart={onDragStart} onDragEnd={onDragEnd} />
            </CalendarStyled.Table>
          </div>
        </CalendarTableOverflowStyled>
        <LoadingCalendar />
        <ModalConfirm ref={modalConfirmRef} />
      </CalendarTableViewStyled>
    </>
  );
};

export default memo(CalendarTableView);

type CalendarTableViewStyledProps = {}
const CalendarTableViewStyled = styled.div<CalendarTableViewStyledProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  flex-grow: 1;
  flex-shrink: 0;
  &.v2 {
    .fixedHigh {
      background: #fff;
    }
    table td .box-time .text-time {
      color: #1D2129;
      text-align: right;
      font-family: Poppins;
      font-size: 13px;
      font-style: normal;
      font-weight: 600;
      line-height: 19.5px; /* 150% */
    }
    table thead th {
      .subtext {
        font-size: 20px;
        text-transform: uppercase;
      }
      // .AnyBodyImage {
      //   width: 32px !important;
      //   height: 32px !important;
      // }
    } 
  }

&.view-full-mode {
  table td .box-time .text-time {
  }
  table th .subtext {
    max-width: 65px;  
  }
  table th .schedule-staff-box {
    display: none;
  }
  table th .ButtonAddMinus {
    display: none;
  }
  table th .staff-th-container {
    padding-left: 0 !important;
    padding-right: 0 !important;
    .AnyBodyImage {
      width: 60px !important;
      height: 60px !important;
    }
  }
  table th .ant-avatar-circle {
    width: 60px !important;
    height: 60px !important;
    line-height: 60px !important;
    font-size: 24px;
    .ant-avatar-string {
      line-height: 60px !important;
    }
  }
  table th, table td {
    min-width: unset;
    max-width: unset;
  }

  table tbody td .appointment-box {
   .WrapperInfoBox {
    min-height: 32px;
   }
    img.icon-info, .AppointmentType{
      display: none !important;
    }
  }
}

`;

const CalendarTableOverflowStyled = styled.div`
position: relative;
  max-height: 100%;
  height:100%;
  z-index: 1;
  max-width: 100%;
  overflow: scroll;
  .calendar {
    position: absolute;
    inset: 0;
  }
  &.drag-scroll-container {
    overflow: auto;
    width: 100%;
    height: 100%;
    user-select: none;
  }

  &.drag-scroll-container.grab {
    cursor: grab;
  }

  &.drag-scroll-container.grabbing {
    cursor: grabbing;
  }
`;